import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import {
  MoreVert as MoreIcon,
  PersonAddAlt as AssignIcon,
  EditOutlined as EditIcon,
  DeleteOutline as DeleteIcon
} from '@mui/icons-material';

/**
 * Acciones de una card de nutrición (asignar, editar, borrar) detrás de un menú de tres puntos.
 *
 * Antes eran tres botones sueltos en la misma fila que el título. En móvil se comían el ancho y el
 * título quedaba reducido a una letra y unos puntos suspensivos, que es justo el dato que hace
 * falta para saber qué card estás mirando. Un solo botón deja sitio al título y las tres acciones
 * siguen a un toque de distancia, con su nombre escrito en lugar de un icono a adivinar.
 */
function NutritionCardActions({ onAssign, onEdit, onDelete }) {
  const { t } = useTranslation(['nutrition', 'buttons']);
  const [anchorEl, setAnchorEl] = useState(null);

  const close = () => setAnchorEl(null);

  // El menú vive dentro de la cabecera de un acordeón: sin frenar la propagación, cada toque
  // plegaría o desplegaría la card además de ejecutar la acción.
  const run = (action) => (event) => {
    event.stopPropagation();
    close();
    action();
  };

  return (
    <>
      <IconButton
        size="small"
        aria-label={t('actions', { ns: 'buttons', defaultValue: 'Acciones' })}
        onClick={(event) => {
          event.stopPropagation();
          setAnchorEl(event.currentTarget);
        }}
        sx={{ flexShrink: 0 }}>
        <MoreIcon fontSize="small" />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={close}
        onClick={(event) => event.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              backgroundColor: 'surface.raised',
              backgroundImage: 'none',
              border: '1px solid',
              borderColor: 'hairline.main',
              borderRadius: '12px',
              minWidth: 190
            }
          }
        }}>
        <MenuItem onClick={run(onAssign)}>
          <ListItemIcon><AssignIcon fontSize="small" sx={{ color: 'gold.main' }} /></ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontWeight: 600 }}>
            {t('assign', { ns: 'nutrition', defaultValue: 'Asignar' })}
          </ListItemText>
        </MenuItem>
        <MenuItem onClick={run(onEdit)}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontWeight: 600 }}>
            {t('edit', { ns: 'buttons', defaultValue: 'Editar' })}
          </ListItemText>
        </MenuItem>
        <MenuItem onClick={run(onDelete)} sx={{ color: 'deleteAction.fg' }}>
          <ListItemIcon><DeleteIcon fontSize="small" sx={{ color: 'deleteAction.fg' }} /></ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontWeight: 600 }}>
            {t('delete', { ns: 'buttons', defaultValue: 'Eliminar' })}
          </ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
}

NutritionCardActions.propTypes = {
  onAssign: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired
};

export default NutritionCardActions;
